import { Alert, Button, Card, FormButtonRow, FormGroup, PageHeader } from '@ksyos/design-system';
import { useState } from 'react';
import type { IFetchResult } from '@frontend/store/fetch';
import { ManagerAuthorization, ValidationErrorCode } from '@common/enums';
import { SuccessFlashWhenSaved } from '@frontend/components/elements/common';
import { FileInput } from '@frontend/components/elements/FileInput';
import { ReprocessInfoModal } from '@frontend/components/elements/ReprocessInfoModal';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization } from '@frontend/utils';

interface IRelationNumbersFile {
    data: string | null;
    fileName: string | null;
}

export function ImportRelationNumbers() {
    const [isLoading, setIsLoading] = useState(false);
    const [successSavedCounter, setSuccessSavedCounter] = useState(0);
    const [showReprocessInfo, setShowReprocessInfo] = useState(false);
    const [relationNumbersFile, setRelationNumbersFile] = useState<IRelationNumbersFile | undefined>();
    const [errorMessage, setErrorMessage] = useState<string | undefined>();
    const [successMessage, setSuccessMessage] = useState<string | undefined>();

    assertHasAuthorization(ManagerAuthorization.importExactRelationData);

    return (
        <PageTemplate width="medium">
            <PageHeader title={translateKey('imports.import')} />

            {errorMessage && <Alert appearance="warning">{errorMessage}</Alert>}
            <SuccessFlashWhenSaved
                message={successMessage}
                savedCounter={successSavedCounter}
            />

            <Card>
                <Card.Header title={translateKey('imports.relationNumbers')}></Card.Header>
                <Card.Body>
                    <FormGroup label={translateKey('common.file')}>
                        <FileInput
                            fileName={relationNumbersFile ? relationNumbersFile.fileName : ''}
                            label={translateKey('upload.selectCsvFile')}
                            onChange={async (result) =>
                                setRelationNumbersFile({
                                    fileName: result.fileName,
                                    data: result.data,
                                })
                            }
                            onError={async (message) => setErrorMessage(message)}
                        />
                    </FormGroup>
                    <FormButtonRow>
                        <Button
                            isDisabled={isLoading}
                            onClick={() => setShowReprocessInfo(true)}
                        >
                            {translateKey('upload.upload')}
                        </Button>
                    </FormButtonRow>

                    {showReprocessInfo && (
                        <ReprocessInfoModal
                            onNo={async () => setShowReprocessInfo(false)}
                            onYes={async () => {
                                setShowReprocessInfo(false);
                                await uploadFile();
                            }}
                        />
                    )}
                </Card.Body>
            </Card>
        </PageTemplate>
    );

    function getErrorMessage(result: IFetchResult): string {
        switch (result.errorCode) {
            case ValidationErrorCode.IMPORT_WRONG_FILE_TYPE: {
                return translateKey('upload.noCsvFormat');
            }
            case ValidationErrorCode.IMPORT_WRONG_FIRST_ROW: {
                return translateKey('upload.wrongFirstRow');
            }
            case ValidationErrorCode.IMPORT_WRONG_TXCS_ID: {
                return translateKey('upload.wrongTxcsId', result.message);
            }
            default: {
                return '';
            }
        }
    }

    async function uploadFile() {
        if (relationNumbersFile && relationNumbersFile.data && relationNumbersFile.fileName) {
            const result = await fetchJson('/api/organizations/importRelationNumbers', 'POST', relationNumbersFile, [
                ValidationErrorCode.IMPORT_WRONG_FILE_TYPE,
                ValidationErrorCode.IMPORT_WRONG_FIRST_ROW,
                ValidationErrorCode.IMPORT_WRONG_TXCS_ID,
            ]);

            if (result.errorCode) {
                setIsLoading(false);
                setErrorMessage(getErrorMessage(result));
            } else {
                setSuccessSavedCounter((counter) => counter + 1);
                setIsLoading(false);
                setSuccessMessage(translateKey('imports.importSuccess', result.json.totalRowsImported));
                setErrorMessage(undefined);
            }
        }
    }
}
